import "focus-visible";
import "nprogress/nprogress.css";
import React, { useEffect } from "react";
import { AppProps } from "next/app";
import Head from "next/head";
import getConfig from "next/config";
import dynamic from "next/dynamic";
import * as Sentry from "@sentry/node";
import { RewriteFrames } from "@sentry/integrations";
import { AuthProvider } from "domain/auth/authContext";
import { NotificationProvider } from "domain/notification/notificationContext";
import { PushNotificationProvider } from "domain/push-notification/pushNotificationContext";
import { CommunityProvider } from "domain/community/communityContext";
import { ConsultationProvider } from "domain/consultations/consultationContext";
import { RequestProvider } from "domain/consultation-request/requestContext";
import { ThemeProvider } from "lib/contexts/themeContext";
import { FirebaseProvider } from "lib/contexts/firebaseContext";
import { LayoutProvider } from "lib/contexts/layoutContext";
import { EventProvider } from "lib/contexts/eventContext";
import Intercom from "lib/components/Intercom";
import { useTranslations } from "lib/i18n";

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  const { serverRuntimeConfig } = getConfig();
  const distDir = `${serverRuntimeConfig.rootDir}/.next`;
  Sentry.init({
    enabled: process.env.NODE_ENV === "production",
    integrations: [
      new RewriteFrames({
        iteratee: (frame) => {
          frame.filename = frame.filename.replace(distDir, "app:///_next");
          return frame;
        },
      }),
    ],
    ignoreErrors: [
      // See lib/components/Prompt.
      "Prevent navigating away",
      // Filter out errors with no value.
      "Error: ResizeObserver loop limit exceeded",
      "Error: Network Error",
      "TypeError: Failed to fetch",
      "TypeError: canceled",
      "TypeError: NetworkError when attempting to fetch resource.",
    ],
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  });
}

const TopProgressBar = dynamic(() => import("lib/components/TopProgressBar"), {
  ssr: false,
});

/**
 * Site-wide fallback metadata, for pages that do not render their own Head.
 * Pages using PageLayout get the fuller MetaDefault from there instead.
 */
const DefaultMeta = () => {
  const t = useTranslations("common");

  return (
    <Head>
      <title>{t("meta.title")}</title>
      <meta name="description" content={t("meta.description")} />
    </Head>
  );
};

const LawfullyApp = ({ Component, pageProps }: AppProps) => {
  useEffect(() => {
    // Remove the server-side injected MUI CSS.
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
  }, []);

  return (
    <>
      <DefaultMeta />

      <ThemeProvider>
        <FirebaseProvider>
          <LayoutProvider>
            <AuthProvider>
              <EventProvider>
                <NotificationProvider>
                  <PushNotificationProvider>
                    <RequestProvider>
                      <ConsultationProvider>
                        <CommunityProvider>
                          <Component {...pageProps} />
                          <Intercom />
                        </CommunityProvider>
                      </ConsultationProvider>
                    </RequestProvider>
                  </PushNotificationProvider>
                </NotificationProvider>
              </EventProvider>
            </AuthProvider>
          </LayoutProvider>
        </FirebaseProvider>
        <TopProgressBar />
      </ThemeProvider>
    </>
  );
};

export default LawfullyApp;
